/**
 * AnimatedPreview
 *
 * Staggered line-by-line stat reveal with motion animations.
 * Shows action-oriented summary: "Here's what we'll do..."
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExpandMore as ExpandMoreIcon, WarningAmber as WarningIcon } from '@mui/icons-material';
import { Box, Button, Collapse, Typography } from '@mui/material';
import { motion } from 'motion/react';

import type { AnimatedPreviewProps, PreviewCounts } from '../ImportReviewWizard.types';

// =============================================================================
// Line Configuration
// =============================================================================

interface PreviewLine {
  text: string;
  boldValue: number;
  color: string;
}

function buildLines(counts: PreviewCounts): PreviewLine[] {
  const lines: PreviewLine[] = [];

  if (counts.newCount > 0) {
    lines.push({
      text: `...create **${counts.newCount}** new patient${counts.newCount === 1 ? '' : 's'}`,
      boldValue: counts.newCount,
      color: 'info.main',
    });
  }

  if (counts.matchedCount > 0) {
    lines.push({
      text: `...update **${counts.matchedCount}** existing patient${counts.matchedCount === 1 ? '' : 's'}`,
      boldValue: counts.matchedCount,
      color: 'success.main',
    });
  }

  if (counts.appointmentCount > 0) {
    lines.push({
      text: `...schedule **${counts.appointmentCount}** appointment${counts.appointmentCount === 1 ? '' : 's'}`,
      boldValue: counts.appointmentCount,
      color: 'primary.main',
    });
  }

  return lines;
}

function renderLineText(text: string, color: string) {
  // Split on **bold** markers
  const parts = text.split(/\*\*(.+?)\*\*/);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <Typography
        key={i}
        component="span"
        sx={{fontWeight: 700,  color }}
      >
        {part}
      </Typography>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

// =============================================================================
// Ambiguous Patients Dropdown
// =============================================================================

interface AmbiguousDropdownProps {
  count: number;
  onResolveNow: () => void;
}

const AmbiguousDropdown = ({ count, onResolveNow }: AmbiguousDropdownProps) => {
  const [expanded, setExpanded] = useState(false);

  const label = `${count} patient${count === 1 ? '' : 's'} couldn't be matched`;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 480,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'warning.50',
        border: 1,
        borderColor: 'warning.200',
      }}
    >
      {/* Collapsed header — clickable */}
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'warning.100' },
          transition: 'background-color 0.15s',
        }}
      >
        <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />
        <Typography variant="body2" sx={{fontWeight: 600,  color: 'warning.dark', flex: 1 }}>
          {label}
        </Typography>
        <ExpandMoreIcon
          sx={{
            color: 'warning.main',
            fontSize: 20,
            transition: 'transform 0.2s',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </Box>

      {/* Expanded content */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {count === 1 ? 'This patient matches' : 'These patients match'} multiple
            existing records and can&apos;t be imported until the duplicates are resolved.
            You can continue with this import now and handle {count === 1 ? 'it' : 'them'} later,
            or resolve the duplicates first and re-upload.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              onClick={onResolveNow}
            >
              Resolve Duplicates Now
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

// =============================================================================
// Component
// =============================================================================

const LINE_DELAY_MS = 600;
const DROPDOWN_DELAY_MS = 400;

const lineVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring' as const, stiffness: 300, damping: 24 },
  },
};

export const AnimatedPreview = ({ counts, onContinue, onCancel }: AnimatedPreviewProps) => {
  const navigate = useNavigate();
  const lines = useMemo(() => buildLines(counts), [counts]);
  const [visibleCount, setVisibleCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const allRevealed = visibleCount >= lines.length;

  // Timer-based staggered reveal
  useEffect(() => {
    if (visibleCount >= lines.length) return;

    const timer = setTimeout(() => {
      setVisibleCount((prev) => prev + 1);
    }, LINE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [visibleCount, lines.length]);

  // Show ambiguous dropdown after lines finish revealing
  const hasAmbiguous = counts.ambiguousCount > 0;
  const hasMixed = hasAmbiguous && lines.length > 0;

  useEffect(() => {
    if (!allRevealed || !hasAmbiguous) return;

    const timer = setTimeout(() => {
      setShowDropdown(true);
    }, DROPDOWN_DELAY_MS);

    return () => clearTimeout(timer);
  }, [allRevealed, hasAmbiguous]);

  // Edge case: no actionable lines at all
  const hasOnlyAmbiguous =
    counts.newCount === 0 &&
    counts.matchedCount === 0 &&
    counts.appointmentCount === 0 &&
    counts.ambiguousCount > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 300,
        py: 6,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Typography
          variant="h5"
          sx={{fontWeight: 600,  mb: 4, textAlign: 'center' }}
        >
          {hasOnlyAmbiguous ? 'We found some issues' : "Here's what we'll do..."}
        </Typography>
      </motion.div>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4, alignItems: 'center' }}>
        {lines.map((line, index) => (
          <motion.div
            key={index}
            variants={lineVariants}
            initial="hidden"
            animate={index < visibleCount ? 'visible' : 'hidden'}
          >
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              {renderLineText(line.text, line.color)}
            </Typography>
          </motion.div>
        ))}

        {/* Ambiguous dropdown — slides in after action lines (mixed case only) */}
        {hasMixed && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={showDropdown
              ? { opacity: 1, y: 0, height: 'auto' }
              : { opacity: 0, y: -10, height: 0 }
            }
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            style={{ overflow: 'hidden', width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <AmbiguousDropdown
              count={counts.ambiguousCount}
              onResolveNow={() => {
                onCancel({ skipNavigation: true });
                navigate('/patients/merge');
              }}
            />
          </motion.div>
        )}
      </Box>

      {/* All-ambiguous: explanation + action buttons */}
      {hasOnlyAmbiguous && allRevealed && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 2, maxWidth: 440 }}>
            These records matched multiple existing patients and need to be resolved first.
            Use Merge Duplicates to resolve them, then re-upload your file to complete the import.
          </Typography>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={allRevealed ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.4 }}
      >
        {hasOnlyAmbiguous ? (
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => {
                onCancel({ skipNavigation: true });
                navigate('/patients/import');
              }}
            >
              Back to Import
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                onCancel({ skipNavigation: true });
                navigate('/patients/merge');
              }}
            >
              Go to Merge Duplicates
            </Button>
          </Box>
        ) : (
          <Button
            variant="contained"
            size="large"
            onClick={onContinue}
            disabled={!allRevealed}
            sx={{ mt: 2 }}
          >
            Continue
          </Button>
        )}
      </motion.div>
    </Box>
  );
};
